import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Label } from "reactstrap";
import Select from "react-select";
import { getApi } from "../helpers/auth_helpers";
import {
  disableProcessFieldMapping,
  listProcessFieldMappingsByField,
  listProcessFieldMappingsByProcess,
  upsertProcessFieldMapping,
} from "./taxonomyApi";
import type { ProcessFieldMapping, ResolvedFieldConfig } from "./types";
import { eligibleForContext, fieldTypeLabel } from "./values";

type ProcessMaster = { _id?: string; id?: string; name: string };

const processId = (item: ProcessMaster) => String(item._id || item.id || "").trim();

type Props = {
  processMasterId?: string;
  taxonomyId?: string;
  fields?: ResolvedFieldConfig[];
  onChange?: () => void;
};

export default function ProcessFieldMappingEditor({ processMasterId, taxonomyId, fields = [], onChange }: Props) {
  const [mappings, setMappings] = useState<ProcessFieldMapping[]>([]);
  const [processes, setProcesses] = useState<ProcessMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const key = processMasterId || taxonomyId;
    if (!key) { setMappings([]); setLoading(false); return; }
    setLoading(true);
    setError("");
    const mappingPromise = processMasterId
      ? listProcessFieldMappingsByProcess(processMasterId)
      : listProcessFieldMappingsByField(taxonomyId || "");
    const [mappingRes, processRes] = await Promise.all([
      mappingPromise,
      taxonomyId ? getApi(`${String(process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "")}/product-process-master/list`) : Promise.resolve(null),
    ]);
    if (mappingRes.success) setMappings(mappingRes.data || []);
    else setError(mappingRes.message || "Process field mappings could not be loaded.");
    if (taxonomyId && processRes?.success && Array.isArray(processRes.data)) setProcesses(processRes.data);
    setLoading(false);
  }, [processMasterId, taxonomyId]);

  useEffect(() => { void load(); }, [load]);

  const save = async (process: string, field: string, patch: Partial<ProcessFieldMapping>) => {
    setSavingId(`${process}:${field}`);
    setError("");
    const res = await upsertProcessFieldMapping({
      processMasterId: process,
      taxonomyId: field,
      ...patch,
    });
    if (!res.success) setError(res.message || "Unable to update this process mapping.");
    else { await load(); onChange?.(); }
    setSavingId("");
  };

  const disable = async (mapping: ProcessFieldMapping) => {
    setSavingId(mapping.id);
    const res = await disableProcessFieldMapping(mapping.id);
    if (!res.success) setError(res.message || "Unable to remove this field from the process.");
    else { await load(); onChange?.(); }
    setSavingId("");
  };

  const enabled = mappings.filter((row) => row.enabled);
  const availableFields = useMemo(() => fields
    .filter((field) => field.enabled && eligibleForContext(field.usedIn, "PROCESS"))
    .filter((field) => !enabled.some((row) => row.taxonomyId === field.taxonomyId))
    .map((field) => ({ value: field.taxonomyId, label: `${field.name} (${fieldTypeLabel(field.fieldType)})` })),
  [fields, enabled]);

  if (!processMasterId && !taxonomyId) {
    return <p className="text-muted small mb-0">Save this Process Master before adding Custom Fields.</p>;
  }
  if (loading) return <p role="status" className="text-muted small">Loading process mappings…</p>;

  if (processMasterId) return <div className="hkp-process-mappings">
    <Label>Fields used in this process</Label>
    {enabled.map((mapping) => {
      const field = fields.find((row) => row.taxonomyId === mapping.taxonomyId);
      return <div className="hkp-process-mapping-row" key={mapping.id}>
        <span>{field?.name || mapping.field?.name || mapping.taxonomyId}</span>
        <Label className="d-flex align-items-center gap-2 mb-0">
          <Input
            type="switch"
            checked={mapping.requiredInProcess}
            disabled={!!savingId}
            onChange={(event) => void save(mapping.processMasterId, mapping.taxonomyId, { requiredInProcess: event.target.checked })}
          />
          Required in this process
        </Label>
        <Button type="button" size="sm" color="danger" outline disabled={!!savingId} onClick={() => void disable(mapping)}>Remove</Button>
      </div>;
    })}
    {availableFields.length ? <Select
      aria-label="Add Custom Field"
      className="React_selector mt-2"
      classNamePrefix="react-select"
      placeholder="Add Custom Field"
      value={null}
      options={availableFields}
      isDisabled={!!savingId}
      onChange={(option: any) => option?.value && void save(processMasterId, option.value, { enabled: true })}
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
      menuPosition="fixed"
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 1300 }) }}
    /> : <p className="text-muted small mb-0">No more enabled Process fields are available.</p>}
    {error ? <p role="alert" className="RequiredField mt-2 mb-0">{error}</p> : null}
  </div>;

  return <div className="hkp-process-mappings">
    <Label>Used in processes</Label>
    {processes.length ? processes.map((process) => {
      const id = processId(process);
      const mapping = mappings.find((row) => row.processMasterId === id);
      const active = !!mapping?.enabled;
      return <div className="hkp-process-mapping-row" key={id || process.name}>
        <span>{process.name}</span>
        <Label className="d-flex align-items-center gap-2 mb-0">
          <Input
            type="switch"
            checked={active}
            disabled={!id || !!savingId}
            onChange={(event) => {
              if (event.target.checked) void save(id, taxonomyId || "", { enabled: true });
              else if (mapping) void disable(mapping);
            }}
          />
          {active ? "Used" : "Not used"}
        </Label>
        {active && mapping ? <Label className="d-flex align-items-center gap-2 mb-0">
          <Input
            type="switch"
            checked={mapping.requiredInProcess}
            disabled={!!savingId}
            onChange={(event) => void save(id, taxonomyId || "", { requiredInProcess: event.target.checked })}
          />
          Required
        </Label> : null}
      </div>;
    }) : <p className="text-muted small">No Process Masters found.</p>}
    {error ? <p role="alert" className="RequiredField mt-2 mb-0">{error}</p> : null}
  </div>;
}
